import React, { useState, useEffect, useRef } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import logo from './logo.png';
import axios from 'axios';

const AUTH0_DOMAIN    = "dev-0namwoly1llouzcm.us.auth0.com";
const AUTH0_CLIENT_ID = "OZYyn6l9NBR7m1vd9uz2NJoGxROus0sh";

// Admin API — uses Auth0 ID token
const adminAPI = (token) => {
  const instance = axios.create({ baseURL: '/api' });
  instance.interceptors.request.use(cfg => {
    if (token) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
  });
  return instance;
};

function fmt(n) {
  if (!n && n !== 0) return '—';
  if (n >= 10000000) return `₹${(n/10000000).toFixed(2)} Cr`;
  if (n >= 100000)   return `₹${(n/100000).toFixed(2)} L`;
  return `₹${Number(n).toLocaleString('en-IN')}`;
}

function AdminInner() {
  const { isLoading, isAuthenticated, loginWithRedirect, logout, getIdTokenClaims, user } = useAuth0();
  const [token,    setToken]    = useState(null);
  const [tab,      setTab]      = useState('clients');
  const [clients,  setClients]  = useState([]);
  const [history,  setHistory]  = useState([]);
  const [clientDetail, setClientDetail] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const [eodRunning, setEodRunning] = useState(false);
  const [eodMsg,   setEodMsg]   = useState('');
  const [sortCol,  setSortCol]  = useState('net_value');
  const [accessDenied, setAccessDenied] = useState(false);
  const [sortDir,  setSortDir]  = useState('desc');

  const timerRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        logout({ logoutParams: { returnTo: window.location.origin + '/admin' } });
        alert('You have been logged out due to 15 minutes of inactivity.');
      }, 15 * 60 * 1000);
    };
    const events = ['mousemove','mousedown','keypress','scroll','touchstart','click'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    // Use ID token — always a signed JWT, never encrypted
    getIdTokenClaims().then(claims => {
      const idToken = claims.__raw; // raw ID token string
      setToken(idToken);
      const api = adminAPI(idToken);
      api.get('/admin/clients').then(r => setClients(r.data.clients)).catch(console.error);
      api.get('/admin/uploads/history').then(r => setHistory(r.data.history)).catch(console.error);
    });
  }, [isAuthenticated]);

  if (isLoading) return (
    <div style={{ minHeight:'100vh', background:'#6B1C2A', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ color:'rgba(255,255,255,0.7)', fontSize:14 }}>Loading...</div>
    </div>
  );

  if (!isAuthenticated) return (
    <div style={{ minHeight:'100vh', background:'#6B1C2A', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
      <img src={logo} alt="Pashupati Capital" style={{ height:52, marginBottom:32 }} />
      <div style={{ background:'#fff', borderRadius:12, padding:'36px 40px', width:380, boxShadow:'0 4px 32px rgba(0,0,0,0.18)' }}>
        <div style={{ fontSize:18, fontWeight:600, color:'#1a1a1a', marginBottom:4 }}>Admin Login</div>
        <div style={{ fontSize:13, color:'#888', marginBottom:24 }}>PCSPL Pledge+ Administration</div>
        <button
          onClick={() => loginWithRedirect({ authorizationParams: { redirect_uri: window.location.origin + '/admin', scope: 'openid profile email' } })}
          style={{ width:'100%', padding:11, background:'#6B1C2A', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer' }}
        >
          Login with Pashupati Capital Account
        </button>
        <div style={{ fontSize:12, color:'#999', textAlign:'center', marginTop:12 }}>
          Use your @pashupaticapital.com email
        </div>
      </div>
    </div>
  );

  const api = adminAPI(token);

  const handleUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setUploadMsg('');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post(`/admin/upload/${type}`, fd, { headers:{'Content-Type':'multipart/form-data'} });
      setUploadMsg(`✓ ${res.data.status === 'success' ? 'Uploaded and processed successfully' : 'Upload failed: ' + res.data.output}`);
      api.get('/admin/uploads/history').then(r => setHistory(r.data.history));
      if (res.data.status === 'success') api.get('/admin/clients').then(r => setClients(r.data.clients));
    } catch (err) {
      setUploadMsg('✗ Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally { setUploading(false); e.target.value = ''; }
  };

  const triggerEod = async () => {
    setEodRunning(true); setEodMsg('Running EOD update...');
    try {
      const res = await api.post('/admin/eod/trigger');
      setEodMsg(res.data.status === 'success' ? '✓ EOD update completed' : '✗ ' + res.data.output);
    } catch (err) { setEodMsg('✗ Failed: ' + err.message); }
    finally { setEodRunning(false); }
  };

  const syncFortnightlyGsheet = async () => {
    setEodRunning(true); setEodMsg('Syncing fortnightly data from Google Sheet...');
    try {
      const res = await api.post('/admin/sync/fortnightly-gsheet');
      setEodMsg(res.data.status === 'success' ? '✓ Fortnightly data synced successfully' : '✗ ' + res.data.output);
    } catch (err) { setEodMsg('✗ Failed: ' + err.message); }
    finally { setEodRunning(false); }
  };

  const syncGoogleSheet = async () => {
    setEodRunning(true); setEodMsg('Syncing from Google Sheet...');
    try {
      const res = await api.post('/admin/sync/google-sheet');
      setEodMsg(res.data.status === 'success' ? '✓ Google Sheet synced successfully' : '✗ ' + res.data.output);
    } catch (err) { setEodMsg('✗ Failed: ' + err.message); }
    finally { setEodRunning(false); }
  };

  const resetPassword = async (code) => {
    if (!window.confirm(`Reset password for client ${code} to default?`)) return;
    try {
      await api.put(`/admin/clients/${code}/reset-password`);
      alert(`Password reset. New password: Pashupati@2026`);
    } catch { alert('Reset failed'); }
  };

  const viewClient = async (code) => {
    const res = await api.get(`/admin/clients/${code}`);
    const marginRes = await api.get(`/admin/clients/${code}/margin-requests`).catch(() => ({ data: { requests: [] } }));
    setClientDetail({ ...res.data, margin_requests: marginRes.data.requests });
    setTab('client_detail');
  };

  const tabs = [{ key:'clients', label:'Clients' }, { key:'upload', label:'Upload Files' }, { key:'history', label:'Upload History' }];
  const tabStyle = (key) => ({
    padding:'8px 18px', borderRadius:6, fontSize:13, fontWeight:500, cursor:'pointer', border:'none',
    background: (tab===key || (tab==='client_detail' && key==='clients')) ? '#6B1C2A' : 'transparent',
    color: (tab===key || (tab==='client_detail' && key==='clients')) ? '#fff' : '#888',
  });

  return (
    <div style={{ minHeight:'100vh', background:'#f5f0ee', display:'flex', flexDirection:'column' }}>
      <div style={{ background:'#6B1C2A', height:56, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 24px', flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <img src={logo} alt="Pashupati Capital" style={{ height:38 }} />
          <div style={{ width:1, height:22, background:'rgba(255,255,255,0.25)' }} />
          <span style={{ color:'#fff', fontSize:14, fontWeight:500 }}>PCSPL Pledge+ — Admin</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ color:'rgba(255,255,255,0.7)', fontSize:13 }}>{user?.name || user?.email}</span>
          <button onClick={() => logout({ logoutParams: { returnTo: window.location.origin + '/admin' } })}
            style={{ padding:'4px 12px', borderRadius:6, border:'1px solid rgba(255,255,255,0.3)', background:'transparent', color:'#fff', fontSize:12, cursor:'pointer' }}>
            Logout
          </button>
        </div>
      </div>

      <div style={{ background:'#fff', borderBottom:'1px solid #f0e8e5', padding:'0 24px', display:'flex', gap:4, alignItems:'center' }}>
        {tabs.map(t => <button key={t.key} style={tabStyle(t.key)} onClick={() => setTab(t.key)}>{t.label}</button>)}
        <div style={{ marginLeft:'auto', fontSize:12, color:'#888', padding:'8px 0' }}>
          {clients.length} clients | Total AUM: {fmt(clients.reduce((a,c) => a+(c.capital_deployed||0),0))}
        </div>
      </div>

      <div style={{ flex:1, padding:24, overflow:'auto' }}>
        {tab === 'clients' && (
          <div style={{ background:'#fff', borderRadius:10, border:'1px solid #f0e8e5', overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'#fdfcfc' }}>
                  {[
                    ['Code','client_code'],['Name','name'],['DP',null],['Inception',null],
                    ['XIRR','xirr'],['Total Capital','total_capital'],['Collateral','net_value'],
                    ['Snapshot',null],['Actions',null]
                  ].map(([h, col]) => (
                    <th key={h} onClick={col ? () => { if(sortCol===col) setSortDir(d=>d==='asc'?'desc':'asc'); else {setSortCol(col);setSortDir('asc');} } : undefined}
                      style={{ fontSize:10, fontWeight:600, color: col&&sortCol===col?'#6B1C2A':'#888', textTransform:'uppercase', letterSpacing:'0.05em', padding:'10px 12px', borderBottom:'1px solid #f0e8e5', textAlign:h==='Actions'?'center':'left', whiteSpace:'nowrap', cursor:col?'pointer':'default', userSelect:'none', background:col&&sortCol===col?'#fdf9f8':'transparent' }}>
                      {h}{col ? (sortCol===col ? (sortDir==='asc'?' ↑':' ↓') : ' ⇅') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...clients].sort((a, b) => {
                  let va, vb;
                  if (sortCol === 'client_code') { va = a.client_code; vb = b.client_code; }
                  else if (sortCol === 'name') { va = (a.name||'').toLowerCase(); vb = (b.name||'').toLowerCase(); return sortDir==='asc' ? va.localeCompare(vb) : vb.localeCompare(va); }
                  else if (sortCol === 'xirr') { va = a.xirr||0; vb = b.xirr||0; }
                  else if (sortCol === 'total_capital') { va = a.total_capital||0; vb = b.total_capital||0; }
                  else if (sortCol === 'net_value') { va = a.net_value||0; vb = b.net_value||0; }
                  else return 0;
                  return sortDir==='asc' ? va-vb : vb-va;
                }).map(c => (
                  <tr key={c.client_code} style={{ borderBottom:'1px solid #f8f4f2' }}
                    onMouseEnter={e=>e.currentTarget.style.background='#fdf9f8'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ padding:'8px 12px', fontSize:12, fontWeight:700, color:'#6B1C2A' }}>{c.client_code}</td>
                    <td style={{ padding:'8px 12px', fontSize:12 }}>
                      {c.name||'—'}
                      {c.is_active !== true && <span style={{ marginLeft:6, fontSize:10, background:'#fee2e2', color:'#c0392b', padding:'2px 6px', borderRadius:4, fontWeight:600 }}>Disabled</span>}
                    </td>

                    <td style={{ padding:'8px 12px', fontSize:11, color:'#888' }}>{c.dp ? String(c.dp).replace('.0','') : '—'}</td>
                    <td style={{ padding:'8px 12px', fontSize:11, color:'#888' }}>{c.inception_date||'—'}</td>
                    <td style={{ padding:'8px 12px', fontSize:12, color:'#6B1C2A', fontWeight:600 }}>{c.xirr ? `${(c.xirr).toFixed(2)}%` : '—'}</td>
                    <td style={{ padding:'8px 12px', fontSize:12, textAlign:'right' }}>{fmt(c.total_capital)}</td>
                    <td style={{ padding:'8px 12px', fontSize:12, textAlign:'right', fontWeight:600 }}>{fmt(c.capital_deployed)}</td>
                    <td style={{ padding:'8px 12px', fontSize:11, color:'#888' }}>{c.snapshot_date||'—'}</td>
                    <td style={{ padding:'8px 12px', textAlign:'center' }}>
                      <div style={{ display:'flex', gap:6, justifyContent:'center' }}>
                        <button onClick={() => viewClient(c.client_code)}
                          style={{ padding:'3px 10px', borderRadius:5, border:'1px solid #e0d0d2', background:'#fff', color:'#6B1C2A', fontSize:11, cursor:'pointer', fontWeight:600 }}>View</button>
                        <button onClick={() => resetPassword(c.client_code)}
                          style={{ padding:'3px 10px', borderRadius:5, border:'1px solid #f0e8e5', background:'#fff', color:'#888', fontSize:11, cursor:'pointer' }}>Reset PW</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'client_detail' && clientDetail && (
          <div>
            <button onClick={() => setTab('clients')}
              style={{ marginBottom:16, padding:'6px 14px', borderRadius:7, border:'1px solid #e0d0d2', background:'#fff', color:'#6B1C2A', fontSize:12, cursor:'pointer', fontWeight:600 }}>
              ← Back to Clients
            </button>
            {clientDetail.margin_requests?.length > 0 && (
              <div style={{ background:'#fff8e1', border:'1px solid #ffe082', borderRadius:10, padding:'12px 16px', marginBottom:16, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div>
                  <div style={{ fontSize:12, fontWeight:600, color:'#856404', marginBottom:2 }}>⚠ Margin Increase Requested</div>
                  <div style={{ fontSize:11, color:'#856404' }}>
                    {clientDetail.margin_requests.filter(r => r.status === 'pending').length} pending request(s) — last: {new Date(clientDetail.margin_requests[0].requested_at).toLocaleString('en-IN')}
                  </div>
                </div>
                {clientDetail.margin_requests[0].status === 'pending' && (
                  <button onClick={async () => {
                    await api.put(`/admin/clients/${clientDetail.client?.client_code}/margin-requests/${clientDetail.margin_requests[0].id}/resolve`);
                    viewClient(clientDetail.client?.client_code);
                  }} style={{ padding:'5px 14px', background:'#166534', color:'#fff', border:'none', borderRadius:6, fontSize:11, cursor:'pointer', fontWeight:600 }}>
                    Mark Resolved
                  </button>
                )}
              </div>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:16 }}>
              {[
                ['Client Code', clientDetail.client?.client_code],
                ['Name', clientDetail.client?.name],
                ['DP', clientDetail.client?.dp ? String(clientDetail.client.dp).replace('.0','') : '—'],
                ['Inception', clientDetail.client?.inception_date],
                ['XIRR', clientDetail.client?.xirr ? `${(clientDetail.client.xirr).toFixed(2)}%` : '—'],
                ['Total Capital', fmt(clientDetail.client?.total_capital)],
                ['Collateral', fmt(clientDetail.client?.capital_deployed)],
                ['Winning Days', `${clientDetail.client?.winning_days||0} / ${clientDetail.client?.total_traded_days||0}`],
                ['Profit Since Inception', fmt(clientDetail.client?.profit_since_inception)],
                ['Available to Pledge', clientDetail.client?.collateral_available > 0 ? fmt(clientDetail.client.collateral_available) : '₹0'],
              ].map(([label, value]) => (
                <div key={label} style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:8, padding:'12px 14px' }}>
                  <div style={{ fontSize:10, color:'#888', marginBottom:4 }}>{label}</div>
                  <div style={{ fontSize:15, fontWeight:600, color:'#333' }}>{value||'—'}</div>
                </div>
              ))}
            </div>
            {clientDetail.monthly_returns?.length > 0 && (
              <div style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:10, padding:'14px 16px', marginBottom:12 }}>
                <div style={{ fontSize:12, fontWeight:600, color:'#333', marginBottom:12 }}>Monthly Performance</div>
                {(() => {
                  const maxAbs = Math.max(...clientDetail.monthly_returns.map(r => Math.abs(r.profit)), 1);
                  const MONTH_SHORT = {APRIL:'Apr',MAY:'May',JUNE:'Jun',JULY:'Jul',AUGUST:'Aug',SEPTEMBER:'Sep',OCTOBER:'Oct',NOVEMBER:'Nov',DECEMBER:'Dec',JANUARY:'Jan',FEBRUARY:'Feb',MARCH:'Mar'};
                  return clientDetail.monthly_returns.map(r => (
                    <div key={r.month} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                      <div style={{ width:28, fontSize:11, color:'#666', textAlign:'right', flexShrink:0 }}>{MONTH_SHORT[r.month]||r.month.slice(0,3)}</div>
                      <div style={{ flex:1, height:18, background:'#f5f0ee', borderRadius:3, overflow:'hidden' }}>
                        <div style={{ width:`${Math.abs(r.profit)/maxAbs*100}%`, height:'100%', background:r.profit>=0?'#6B1C2A':'#e74c3c', borderRadius:3 }} />
                      </div>
                      <div style={{ width:80, fontSize:11, textAlign:'right', flexShrink:0, color:r.profit>=0?'#6B1C2A':'#e74c3c', fontWeight:600 }}>
                        {r.profit>=10000000?`₹${(r.profit/10000000).toFixed(2)}Cr`:r.profit>=100000?`₹${(r.profit/100000).toFixed(2)}L`:`₹${Number(r.profit).toLocaleString('en-IN')}`}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}

            <div style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:10, overflow:'hidden' }}>
              <div style={{ padding:'12px 16px', borderBottom:'1px solid #f0e8e5', fontSize:12, fontWeight:600, color:'#333' }}>
                Holdings ({clientDetail.holdings?.length||0} scrips)
              </div>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ background:'#fdfcfc' }}>
                    {['Scrip','Name','Qty','Price','Market Value','Haircut','Collateral'].map(h=>(
                      <th key={h} style={{ fontSize:10, fontWeight:600, color:'#888', textTransform:'uppercase', padding:'8px 12px', borderBottom:'1px solid #f0e8e5', textAlign:'left', letterSpacing:'0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(clientDetail.holdings||[]).map((h,i)=>(
                    <tr key={i} style={{ borderBottom:'1px solid #f8f4f2' }}>
                      <td style={{ padding:'7px 12px', fontSize:11, fontWeight:700, color:'#6B1C2A' }}>{h.scrip_code}</td>
                      <td style={{ padding:'7px 12px', fontSize:11 }}>{h.scrip_name||'—'}</td>
                      <td style={{ padding:'7px 12px', fontSize:11, textAlign:'right' }}>{Number(h.net_quantity||0).toLocaleString('en-IN')}</td>
                      <td style={{ padding:'7px 12px', fontSize:11, textAlign:'right' }}>{h.price?Number(h.price).toFixed(2):'—'}</td>
                      <td style={{ padding:'7px 12px', fontSize:11, textAlign:'right' }}>{fmt(h.market_value)}</td>
                      <td style={{ padding:'7px 12px', fontSize:11, textAlign:'right', color:'#888' }}>{h.haircut!=null?`${h.haircut}%`:'—'}</td>
                      <td style={{ padding:'7px 12px', fontSize:11, fontWeight:600, color:'#6B1C2A', textAlign:'right' }}>{fmt(h.gross_collateral_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'upload' && (
          <div style={{ maxWidth:600 }}>
            {uploadMsg && (
              <div style={{ padding:'12px 16px', borderRadius:8, marginBottom:16, fontSize:13,
                background: uploadMsg.startsWith('✓') ? '#f0fdf4' : '#fdf0f0',
                border: `1px solid ${uploadMsg.startsWith('✓') ? '#86efac' : '#f5c6c6'}`,
                color: uploadMsg.startsWith('✓') ? '#166534' : '#c0392b' }}>
                {uploadMsg}
              </div>
            )}
            {[
              { type:'fortnightly', title:'Fortnightly Report', desc:'Upload a new fortnightly pledge file. Saved to reports folder and processed automatically.' },
              { type:'monthly',     title:'Monthly Report',     desc:'Upload updated monthly report. Overwrites existing file and refreshes client XIRR and stats.' },
            ].map(({ type, title, desc }) => (
              <div key={type} style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:10, padding:20, marginBottom:16 }}>
                <div style={{ fontSize:14, fontWeight:600, color:'#333', marginBottom:4 }}>{title}</div>
                <div style={{ fontSize:12, color:'#888', marginBottom:14 }}>{desc}</div>
                <label style={{ display:'inline-block', padding:'8px 18px', background:'#6B1C2A', color:'#fff', borderRadius:7, fontSize:13, fontWeight:600, cursor: uploading?'not-allowed':'pointer', opacity: uploading?0.6:1 }}>
                  {uploading ? 'Processing...' : `Choose ${title}`}
                  <input type="file" accept=".xlsx,.xls" style={{ display:'none' }} disabled={uploading} onChange={e => handleUpload(e, type)} />
                </label>
              </div>
            ))}
            <div style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:10, padding:20, marginBottom:16 }}>
              <div style={{ fontSize:14, fontWeight:600, color:'#333', marginBottom:4 }}>Sync Fortnightly from Google Sheet</div>
              <div style={{ fontSize:12, color:'#888', marginBottom:14 }}>Read all date-named sheets from the fortnightly pledge Google Sheet and update holdings and portfolio trend data for all Pledge+ clients.</div>
              {eodMsg && eodMsg.includes('ortnightly') && <div style={{ fontSize:12, color: eodMsg.startsWith('✓')?'#166534':'#c0392b', marginBottom:10 }}>{eodMsg}</div>}
              <button onClick={syncFortnightlyGsheet} disabled={eodRunning}
                style={{ padding:'8px 18px', background:'#1a4d7a', color:'#fff', borderRadius:7, fontSize:13, fontWeight:600, border:'none', cursor: eodRunning?'not-allowed':'pointer', opacity: eodRunning?0.6:1, display:'flex', alignItems:'center', gap:6 }}>
                <span>📈</span> {eodRunning ? 'Syncing...' : 'Sync Fortnightly from Google Sheet'}
              </button>
            </div>

            <div style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:10, padding:20, marginBottom:16 }}>
              <div style={{ fontSize:14, fontWeight:600, color:'#333', marginBottom:4 }}>Sync from Google Sheet</div>
              <div style={{ fontSize:12, color:'#888', marginBottom:14 }}>Pull latest monthly report data directly from Google Sheets and update all client stats, XIRR and monthly returns.</div>
              {eodMsg && eodMsg.includes('Google') && <div style={{ fontSize:12, color: eodMsg.startsWith('✓')?'#166534':'#c0392b', marginBottom:10 }}>{eodMsg}</div>}
              <button onClick={syncGoogleSheet} disabled={eodRunning}
                style={{ padding:'8px 18px', background:'#1a7340', color:'#fff', borderRadius:7, fontSize:13, fontWeight:600, border:'none', cursor: eodRunning?'not-allowed':'pointer', opacity: eodRunning?0.6:1, display:'flex', alignItems:'center', gap:6 }}>
                <span>📊</span> {eodRunning ? 'Syncing...' : 'Sync from Google Sheet'}
              </button>
            </div>

            <div style={{ background:'#fff', border:'1px solid #f0e8e5', borderRadius:10, padding:20 }}>
              <div style={{ fontSize:14, fontWeight:600, color:'#333', marginBottom:4 }}>Manual EOD Price Update</div>
              <div style={{ fontSize:12, color:'#888', marginBottom:14 }}>Fetch latest prices from XTS and recompute all portfolio values right now.</div>
              {eodMsg && !eodMsg.includes('Google') && <div style={{ fontSize:12, color: eodMsg.startsWith('✓')?'#166534':'#c0392b', marginBottom:10 }}>{eodMsg}</div>}
              <button onClick={triggerEod} disabled={eodRunning}
                style={{ padding:'8px 18px', background:'#6B1C2A', color:'#fff', borderRadius:7, fontSize:13, fontWeight:600, border:'none', cursor: eodRunning?'not-allowed':'pointer', opacity: eodRunning?0.6:1 }}>
                {eodRunning ? 'Running...' : 'Run EOD Update Now'}
              </button>
            </div>
          </div>
        )}

        {tab === 'history' && (
          <div style={{ background:'#fff', borderRadius:10, border:'1px solid #f0e8e5', overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'#fdfcfc' }}>
                  {['Date & Time','Type','Filename','Status','Notes'].map(h=>(
                    <th key={h} style={{ fontSize:10, fontWeight:600, color:'#888', textTransform:'uppercase', padding:'10px 12px', borderBottom:'1px solid #f0e8e5', textAlign:'left', letterSpacing:'0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.length===0 ? (
                  <tr><td colSpan={5} style={{ padding:20, textAlign:'center', color:'#aaa', fontSize:13 }}>No uploads yet</td></tr>
                ) : history.map((h,i)=>(
                  <tr key={i} style={{ borderBottom:'1px solid #f8f4f2' }}>
                    <td style={{ padding:'8px 12px', fontSize:11, color:'#888', whiteSpace:'nowrap' }}>{new Date(h.uploaded_at).toLocaleString('en-IN')}</td>
                    <td style={{ padding:'8px 12px' }}>
                      <span style={{ padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:600,
                        background: h.file_type==='monthly'?'#fdf0f2':'#f0f4ff',
                        color: h.file_type==='monthly'?'#6B1C2A':'#1a3a6B' }}>
                        {h.file_type}
                      </span>
                    </td>
                    <td style={{ padding:'8px 12px', fontSize:11 }}>{h.filename}</td>
                    <td style={{ padding:'8px 12px' }}>
                      <span style={{ padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:600,
                        background: h.status==='success'?'#f0fdf4':'#fdf0f0',
                        color: h.status==='success'?'#166534':'#c0392b' }}>
                        {h.status}
                      </span>
                    </td>
                    <td style={{ padding:'8px 12px', fontSize:11, color:'#888', maxWidth:300, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{h.notes||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminApp() {
  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin + '/admin',
        scope: "openid profile email",
      }}
    >
      <AdminInner />
    </Auth0Provider>
  );
}
